<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';

import { GAME_ICONS, NOTIFY_PLATES, PAGE_ART, TITLE_CRESTS } from '@/assets/art/art';
import { UI_ICONS } from '@/assets/sprites/manifest';
import { play, setScene } from '@/audio';
import { setMusicAllowed } from '@/audio/music';
import { IS_FORUM_HOST } from '@/router';
import ChatDock from '@/components/ChatDock.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import ResourceBar from '@/components/ResourceBar.vue';
import TitleCrest from '@/components/TitleCrest.vue';
import { useTitlesStore } from '@/stores/titles';
import { useRealmStore } from '@/stores/realm';
import { useUnreadStore } from '@/stores/unread';
import SettingsPanel from '@/components/SettingsPanel.vue';
import TooltipLayer from '@/components/TooltipLayer.vue';
import TutorialGuide from '@/components/TutorialGuide.vue';
import { installUiSounds } from '@/composables/useUiSounds';
import { useConfirmStore } from '@/stores/confirm';
import { useSettingsStore } from '@/stores/settings';
import { useWebSocket } from '@/composables/useWebSocket';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notifications';
import { useVillageStore } from '@/stores/village';
import { formatCoords, formatNumber } from '@/utils/format';

const auth = useAuthStore();
const villages = useVillageStore();
const notifications = useNotificationStore();
const titles = useTitlesStore();
const unread = useUnreadStore();
const realm = useRealmStore();
const route = useRoute();
const router = useRouter();
const { connected, connect, disconnect } = useWebSocket();

/**
 * The style without its rank word, since the crest beside it already says
 * which rung this is. "Duke of Cranbach" in a crowded bar is mostly the word
 * Duchy repeated: the label reads better as the place alone.
 */
const shortStyle = computed(() => {
  const name = titles.style?.name ?? '';
  return name.replace(
    /^(Empire|Kingdom|Duchy|County|Crown|Dogeship|Patrimony|Sultanate|Caliphate|Emirate|Principality|Despotate|Lordship|Grand Principality|Atabegate|Bishopric|Judicate) of /,
    '',
  );
});

/** Everything held beyond the one being shown. */
const titleExtra = computed(() => {
  const c = titles.counts;
  return c.county + c.duchy + c.kingdom + c.empire - 1;
});

const titleTip = computed(() => {
  const c = titles.counts;
  const parts: string[] = [];
  if (c.empire) parts.push(`${c.empire} empire${c.empire === 1 ? '' : 's'}`);
  if (c.kingdom) parts.push(`${c.kingdom} kingdom${c.kingdom === 1 ? '' : 's'}`);
  if (c.duchy) parts.push(`${c.duchy} ${c.duchy === 1 ? 'duchy' : 'duchies'}`);
  if (c.county) parts.push(`${c.county} count${c.county === 1 ? 'y' : 'ies'}`);
  return `${parts.join(', ')}. Open the Titles page.`;
});

/**
 * `icon` is optional on purpose. Simulator has no artwork yet, and a repeated
 * icon reads worse than an honest gap, so it stays text-only until one exists.
 */
const NAV: { name: string; label: string; icon?: string }[] = [
  { name: 'village', label: 'Village', icon: GAME_ICONS.buildings },
  { name: 'map', label: 'Map', icon: GAME_ICONS.world },
  // The crest is its own icon: the county frame, at nav size.
  { name: 'titles', label: 'Titles', icon: TITLE_CRESTS.county.src },
  { name: 'military', label: 'Military', icon: GAME_ICONS.military },
  { name: 'market', label: 'Market', icon: GAME_ICONS.market },
  { name: 'shop', label: 'Shop' },
  { name: 'quests', label: 'Tasks', icon: GAME_ICONS.scroll },
  { name: 'reports', label: 'Reports', icon: GAME_ICONS.reports },
  // Letters, and the offers of fealty that arrive as letters.
  { name: 'inbox', label: 'Letters', icon: GAME_ICONS.letters },
  { name: 'profile', label: 'Profile', icon: GAME_ICONS.portrait },
  { name: 'alliance', label: 'Alliance', icon: GAME_ICONS.alliance },
  { name: 'leaderboard', label: 'Rankings', icon: GAME_ICONS.points },
  { name: 'ai-log', label: 'Stewards' },
];

/** Admin is appended rather than listed: most players must never see it. */
const NAV_ADMIN = { name: 'admin', label: 'Admin' };

// The lobby is before the world, so it carries no world chrome: no resource
// bar for a village you have not entered, no nav for screens that assume one.
// The forum host never shows chrome at all, signed in or not: it is its own
// website, and the game's shell around a thread would make it a page again.
const CHROMELESS = new Set(['login', 'worlds']);
const showChrome = computed(
  () => !IS_FORUM_HOST && auth.isAuthenticated && !CHROMELESS.has(String(route.name)),
);
const current = computed(() => villages.current);

/**
 * Map and village are viewports onto the world, not documents. They get the
 * whole window instead of the reading-width column the data screens use.
 */
const WIDE_ROUTES = new Set(['map', 'village']);
const wide = computed(() => WIDE_ROUTES.has(String(route.name)));

// Tilt the ambience to match where the player is: sheltered in the village,
// more open air out on the map. Signed out there is no ambience at all - the
// login page and the public forum are pages, not places, and a visitor who
// has not entered the world should not be hearing it.
watch(
  () => [route.name, auth.isAuthenticated] as const,
  ([name, isIn]) => {
    // The forum site is a website, not a place in the world: no score, no
    // ambience, whoever is signed in.
    setMusicAllowed(isIn && !IS_FORUM_HOST);
    if (!isIn || IS_FORUM_HOST) setScene('silence');
    else if (name === 'village') setScene('village');
    else if (name === 'map') setScene('map');
    else setScene('menu');
  },
  { immediate: true },
);

watch(
  () => auth.isAuthenticated,
  async (isIn) => {
    // The forum site never loads the world; it only reads and writes threads.
    if (IS_FORUM_HOST) return;
    if (isIn) {
      await villages.loadVillages();
      void titles.refresh();
      connect();
    } else {
      disconnect();
      villages.reset();
    }
  },
  { immediate: true },
);

async function switchVillage(event: Event): Promise<void> {
  const id = (event.target as HTMLSelectElement).value;
  await villages.select(id);
}

const renaming = ref(false);
const nameDraft = ref('');
const renameInput = ref<HTMLInputElement | null>(null);

async function startRename(): Promise<void> {
  nameDraft.value = current.value?.name ?? '';
  renaming.value = true;
  await nextTick();
  renameInput.value?.select();
}

async function commitRename(): Promise<void> {
  const id = villages.currentId;
  if (!id) return;
  const ok = await villages.rename(id, nameDraft.value);
  if (ok) renaming.value = false;
}

const confirm = useConfirmStore();

async function signOut(): Promise<void> {
  const ok = await confirm.ask({
    title: 'Sign out?',
    body: 'Your village keeps building and your armies keep marching while you are away.',
    confirmLabel: 'Sign out',
  });
  if (!ok) return;

  await auth.logout();
  disconnect();
  villages.reset();
  await router.push({ name: 'login' });
}

const settings = useSettingsStore();

/** On the real game domain the forum lives on its own subdomain. */
const forumIsExternal =
  location.hostname.endsWith('waldermarch.com') && !location.hostname.startsWith('forum.');

function openSettings(): void {
  play('open');
  settings.open = true;
}

/**
 * The lord's own figures are polled slowly, because they change slowly.
 *
 * Prestige accrues by the hour and lands in lumps after a battle, the domain
 * count changes when land does, and an offer of fealty is the one piece of
 * post that genuinely cannot wait. None of that is worth a socket message, and
 * all of it was previously read once at startup, so the badges in the top bar
 * sat at whatever they were when the page loaded until it was reloaded.
 *
 * A minute is frequent enough for all three and cheap enough to leave running.
 */
const REALM_POLL_MS = 60_000;
let realmTimer: number | null = null;

function refreshRealm(): void {
  void realm.load(true);
  void realm.loadLetters(true);
}

/**
 * A tab that has been in the background for an hour is an hour out of date,
 * and waiting up to a minute after coming back to it is exactly when a stale
 * number is most obvious. Refresh on return rather than only on the timer.
 */
function onVisible(): void {
  if (document.visibilityState === 'visible' && auth.isAuthenticated) refreshRealm();
}

/*
 * Watched, not checked once.
 *
 * This used to run only inside onMounted behind an isAuthenticated guard,
 * which is correct for a page refresh with a stored session and silently
 * wrong for the other way in: log in fresh and the App was already mounted
 * with the guard false, so the realm profile never loaded, the prestige and
 * domain badges sat empty, and nothing fixed it until some view happened to
 * load the profile as a side effect. The watch fires on both paths.
 */
function startRealm(): void {
  if (realmTimer !== null) return;
  // The first read is not quiet: if the profile cannot be fetched at all,
  // that is worth saying once.
  void realm.load();
  void realm.loadLetters();
  realmTimer = window.setInterval(refreshRealm, REALM_POLL_MS);
}

function stopRealm(): void {
  if (realmTimer === null) return;
  window.clearInterval(realmTimer);
  realmTimer = null;
}

watch(
  () => auth.isAuthenticated,
  (authed) => {
    // The forum host renders threads, not the world: no socket, no polling.
    if (IS_FORUM_HOST) return;
    if (authed) {
      connect();
      startRealm();
    } else {
      stopRealm();
    }
  },
);

onMounted(() => {
  installUiSounds();
  if (auth.isAuthenticated && !IS_FORUM_HOST) {
    connect();
    startRealm();
  }
  document.addEventListener('visibilitychange', onVisible);
});
onBeforeUnmount(() => {
  stopRealm();
  document.removeEventListener('visibilitychange', onVisible);
  disconnect();
});
</script>

<template>
  <div class="app">
    <header v-if="showChrome" class="topbar">
      <RouterLink class="topbar__brand" :to="{ name: 'village' }">
        <img :src="PAGE_ART.wordmark" alt="Waldermarch" height="26" />
      </RouterLink>

      <nav class="topbar__nav">
        <RouterLink v-for="item in NAV" :key="item.name" :to="{ name: item.name }">
          <img v-if="item.icon" :src="item.icon" alt="" aria-hidden="true" />
          {{ item.label }}
          <!-- Unread battle reports. Only ever on Reports, and only when
               there is something there that has not been read. -->
          <span
            v-if="item.name === 'reports' && unread.reports > 0"
            class="nav__badge"
            :title="`${unread.reports} unread`"
          >{{ unread.reports > 99 ? '99+' : unread.reports }}</span>
          <!-- Unread letters, counted the same way. An offer of fealty sitting
               unanswered is the one piece of post that genuinely cannot wait,
               so it wears the same badge as a battle report. -->
          <span
            v-if="item.name === 'inbox' && realm.unread > 0"
            class="nav__badge"
            :title="`${realm.unread} unread`"
          >{{ realm.unread > 99 ? '99+' : realm.unread }}</span>
        </RouterLink>
        <RouterLink v-if="auth.user?.isAdmin" :to="{ name: NAV_ADMIN.name }">
          {{ NAV_ADMIN.label }}
        </RouterLink>
      </nav>

      <div class="topbar__user">
        <span
          class="topbar__link-state"
          :class="{ 'is-live': connected }"
          :title="connected ? 'Live connection to the server' : 'Reconnecting…'"
        >
          {{ connected ? 'live' : 'offline' }}
        </span>
        <span class="dim">{{ auth.user?.username }}</span>
        <button class="topbar__settings" title="Settings" @click="openSettings">
          <img :src="UI_ICONS.settings" alt="Settings" width="22" height="22" />
        </button>
        <button class="btn btn--sm" @click="signOut">Sign out</button>
      </div>
    </header>

    <div v-if="showChrome && current" class="villagebar">
      <select
        v-if="villages.villages.length > 1"
        class="villagebar__select"
        :value="villages.currentId ?? ''"
        @change="switchVillage"
      >
        <option v-for="v in villages.villages" :key="v.id" :value="v.id">
          {{ v.name }} {{ formatCoords(v.x, v.y) }}
        </option>
      </select>

      <form v-if="renaming" class="villagebar__rename" @submit.prevent="commitRename">
        <input
          ref="renameInput"
          v-model="nameDraft"
          maxlength="32"
          required
          aria-label="Village name"
          @keyup.esc="renaming = false"
        />
        <button class="btn btn--sm btn--primary" type="submit">Save</button>
        <button class="btn btn--sm" type="button" @click="renaming = false">Cancel</button>
      </form>

      <div v-else class="villagebar__name">
        <template v-if="villages.villages.length === 1">{{ current.name }}</template>
        <span class="muted">{{ formatCoords(current.x, current.y) }}</span>
        <button class="villagebar__edit" title="Rename this village" @click="startRename">
          Rename
        </button>
      </div>

      <span class="stat" :data-tip="`${current.points} points: the worth of everything this village has built.`">
        <img :src="GAME_ICONS.points" alt="Points" width="20" />
        {{ formatNumber(current.points) }}
      </span>
      <span class="stat" :data-tip="`Loyalty ${Math.floor(current.loyalty)} of 100. Bailiffs break it; at zero the village changes hands.`">
        <img :src="GAME_ICONS.loyalty" alt="Loyalty" width="18" />
        {{ Math.floor(current.loyalty) }}
      </span>

      <!--
        Domain and prestige, beside loyalty.
        These three are the realm at a glance: how loyal the land is, how much
        of it one lord can actually govern, and what he has earned the right to
        take next. They belong together and they belong on every screen, which
        is why they sit in the bar rather than only on the profile page.
      -->
      <RouterLink
        v-if="realm.domain"
        :to="{ name: 'profile' }"
        class="stat stat--link"
        :class="{ 'stat--warn': realm.domain.over > 0 }"
        :data-tip="
          realm.domain.over > 0
            ? `${realm.domain.held} holdings against a limit of ${realm.domain.limit}. ` +
              `${realm.domain.over} over: production down ` +
              `${Math.round((1 - realm.domain.productionMultiplier) * 100)}% and every holding ` +
              `bleeds ${realm.domain.loyaltyDrainPerHour.toFixed(1)} loyalty an hour. Take a vassal ` +
              `or give land away.`
            : `${realm.domain.held} of ${realm.domain.limit} holdings. Land held by a vassal ` +
              `counts toward your realm but not against this.`
        "
      >
        <img :src="GAME_ICONS.domain" alt="Domain" width="18" />
        {{ realm.domain.held }}/{{ realm.domain.limit }}
      </RouterLink>
      <!--
        Only once the profile has actually arrived. The computed falls back
        to zero while it is loading, and a badge that says 0 is an answer,
        not a placeholder: it read as prestige being wiped every time the
        profile was slow or a load quietly failed.
      -->
      <RouterLink
        v-if="realm.profile"
        :to="{ name: 'profile' }"
        class="stat stat--link"
        :data-tip="`${formatNumber(realm.prestige)} prestige. Won in battle, spent forging claims; without a claim you may raid a county but never take it.`"
      >
        <img :src="GAME_ICONS.prestige" alt="Prestige" width="18" />
        {{ formatNumber(realm.prestige) }}
      </RouterLink>

      <!--
        What the player is called, between what they own and what they have.
        Most players will never hold more than a county, so this shows the
        grandest style they have and the tally beside it rather than a list
        that would be one line long forever.
      -->
      <RouterLink
        v-if="titles.style"
        class="stat titlestat"
        :to="{ name: 'titles' }"
        :data-tip="titleTip"
      >
        <TitleCrest :tier="titles.style.tier" :name="titles.style.name" :size="26" />
        <span class="titlestat__text">
          <span class="titlestat__rank">{{ titles.style.rank }} of</span>
          <strong class="titlestat__name">{{ shortStyle }}</strong>
        </span>
        <span v-if="titleExtra" class="titlestat__more">+{{ titleExtra }}</span>
      </RouterLink>

      <div class="spacer"></div>
      <ResourceBar />
    </div>

    <main
      class="content"
      :class="{ 'content--bare': !showChrome, 'content--wide': showChrome && wide }"
    >
      <!--
        Deliberately NOT kept alive. KeepAlive on the map and village was
        tried for instant tab switching and shipped black screens: both
        views paint into canvases whose size and context do not survive
        being detached and reattached. If that optimisation returns, it
        returns with onActivated repaint hooks and testing, not hope.
      -->
      <RouterView />
    </main>

    <!-- Bottom left, opposite the chat dock. -->
    <div
      class="toasts"
      role="status"
      aria-live="polite"
    >
      <div
        v-for="note in notifications.items"
        :key="note.id"
        class="toast"
        :class="[`toast--${note.level}`, `toast--${note.kind}`]"
        :style="{ backgroundImage: `url(${NOTIFY_PLATES[note.kind]?.src ?? NOTIFY_PLATES.scroll.src})` }"
        @click="notifications.dismiss(note.id)"
      >
        {{ note.message }}
      </div>
    </div>

    <ConfirmDialog />
    <SettingsPanel />
    <!-- Above every panel and dock, because it explains the things in them. -->
    <TooltipLayer />
    <TutorialGuide v-if="showChrome" />
    <ChatDock v-if="showChrome" />

    <footer v-if="showChrome" class="footer">
      <span>Waldermarch - grounded medieval strategy</span>
      <!--
        Reference rather than play. The forum and the manual are things you
        read, not things you do to your realm, and they were taking two slots
        in a bar that has to hold everything you act on.
      -->
      <span class="muted">
        <!-- The forum is its own site on the real domain; in dev and on any
             other host it stays an in-app route. -->
        <a v-if="forumIsExternal" href="https://forum.waldermarch.com" class="footer__link">Forum</a>
        <RouterLink v-else to="/forum" class="footer__link">Forum</RouterLink>
        ·
        <RouterLink to="/simulator" class="footer__link">Simulator</RouterLink>
        ·
        <RouterLink to="/guide" class="footer__link">Manual</RouterLink>
        ·
        <RouterLink to="/credits" class="footer__link">Credits</RouterLink>
        · v0.1.0
      </span>
    </footer>
  </div>
</template>

<style scoped lang="scss">
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.topbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.45rem 1rem;
  background: linear-gradient(180deg, var(--stone-750), var(--stone-800));
  border-bottom: 1px solid var(--border-strong);
  position: sticky;
  top: 0;
  z-index: 30;

  &__brand {
    display: flex;
    align-items: center;
    flex: none;

    img {
      display: block;
      width: auto;
      filter: drop-shadow(0 1px 3px rgb(0 0 0 / 60%));
    }
  }

  &__nav {
    display: flex;
    gap: 0.15rem;
    flex: 1;
    overflow-x: auto;

    a {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      padding: 0.25rem 0.7rem;
      border-radius: var(--radius);
      font-size: 0.88rem;
      color: var(--parchment-dim);
      white-space: nowrap;
      border: 1px solid transparent;

      img {
        display: block;
        /* A fixed box, not a fixed height. The icons have very different
           aspect ratios (the scroll is nearly 2.5:1), so sizing by height
           alone let it sprawl next to the squarer ones. */
        width: 22px;
        height: 22px;
        object-fit: contain;
        /* Dimmed to match the label, brightening together on hover. */
        opacity: 0.72;
        filter: drop-shadow(0 1px 1px rgb(0 0 0 / 55%));
        transition: opacity 120ms ease;
      }

      &:hover {
        color: var(--parchment);
        background: rgb(255 255 255 / 4%);

        img { opacity: 1; }
      }

      &.router-link-active {
        color: var(--parchment);
        background: var(--stone-900);
        border-color: var(--border-strong);

        img { opacity: 1; }
      }
    }
  }

  &__user {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.85rem;
  }

  &__settings {
    display: flex;
    padding: 0.15rem;
    background: none;
    border: 1px solid transparent;
    border-radius: var(--radius);
    cursor: pointer;

    img { display: block; opacity: 0.75; transition: opacity 120ms ease; }

    &:hover {
      border-color: var(--border-strong);
      img { opacity: 1; }
    }
  }

  &__link-state {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--bad);

    &.is-live { color: var(--ok); }
  }
}

/*
 * The style, sat with the village's own numbers. Deliberately quiet: it is a
 * standing fact rather than something that changes minute to minute, and the
 * resources beside it have first claim on the eye.
 */
/* Small, round, and the one red thing in the bar, so it reads as "something
   happened" without shouting. */
.nav__badge {
  display: inline-block;
  min-width: 1.15em;
  margin-left: 0.3rem;
  padding: 0 0.28em;
  border-radius: 999px;
  background: var(--bad, #b4503f);
  color: #f6e6dc;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
  vertical-align: 0.1em;
}

.titlestat {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.1rem 0.5rem 0.1rem 0.25rem;
  border: 1px solid rgb(196 164 96 / 22%);
  border-radius: 999px;
  background: rgb(196 164 96 / 7%);
  text-decoration: none;
  color: inherit;
  transition:
    background 140ms ease,
    border-color 140ms ease;

  &:hover {
    background: rgb(196 164 96 / 15%);
    border-color: rgb(196 164 96 / 45%);
  }

  &__text {
    display: flex;
    flex-direction: column;
    line-height: 1.05;
  }

  &__rank {
    font-size: 0.58rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgb(212 184 126 / 70%);
  }

  &__name {
    font-family: var(--font-display);
    font-size: 0.86rem;
    color: var(--gold, #d8b96a);
    max-width: 13ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__more {
    font-size: 0.7rem;
    color: rgb(212 184 126 / 65%);
  }
}

.villagebar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  padding: 0.4rem 1rem;
  background: var(--stone-850);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 43px;
  z-index: 20;

  &__select { width: auto; min-width: 200px; }

  &__name {
    display: flex;
    align-items: baseline;
    gap: 0.45rem;
    font-family: var(--font-display);
    font-size: 1rem;
  }

  &__edit {
    padding: 0;
    background: none;
    border: none;
    font: inherit;
    font-family: var(--font-body);
    font-size: 0.72rem;
    color: var(--parchment-faint);
    text-decoration: underline dotted;
    cursor: pointer;

    &:hover { color: var(--gold); }
  }

  &__rename {
    display: flex;
    align-items: center;
    gap: 0.3rem;

    input { width: 200px; }
  }
}

.stat--link {
  text-decoration: none;
  color: inherit;
}

.stat--warn {
  color: #e2705c;
}

.stat {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.1rem 0.45rem 0.1rem 0.3rem;
  background: var(--stone-900);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;

  img {
    display: block;
    height: auto;
    filter: drop-shadow(0 1px 1px rgb(0 0 0 / 50%));
  }
}

.content {
  flex: 1;
  padding: 1rem;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;

  &--bare { display: grid; place-items: center; }

  /* Full bleed for the world views. The reading-width column is right for
     tables and reports and pure waste for a map. */
  &--wide {
    max-width: none;
    padding: 0.5rem;
  }
}

.footer__link {
  color: inherit;
  text-decoration: none;

  &:hover {
    color: #e8c46a;
    text-decoration: underline;
  }
}

.footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.5rem 1rem;
  /*
   * The chat dock is fixed to the bottom-right corner, directly on top of
   * this bar's right half. The reference links lived under it, present in
   * the markup and invisible on every screen wide enough to play on.
   * Reserving the dock's own width keeps them clear of it.
   */
  padding-right: calc(min(340px, 100vw - 1.5rem) + 1.75rem);
  border-top: 1px solid var(--border);
  background: var(--stone-850);
  font-size: 0.78rem;
  color: var(--parchment-faint);
}

.toasts {
  position: fixed;
  left: 0.75rem;
  bottom: 0.75rem;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: min(360px, calc(100vw - 1.5rem));
}

/*
 * The knight owns the bottom-left for the first ten minutes, so while he is
 * out the toasts go back to the right and clear the collapsed chat bar the way
 * they used to. Better than guessing his height and stacking above him.
 */
body.has-tutor .toasts {
  left: auto;
  right: 0.75rem;
  bottom: 2.75rem;
}

/*
 * Each toast is painted on one of the ribboned plates from the
 * notifications_quick sheet: badge on the left, message on the bar. The
 * plate is the whole visual, so there is no box, border or backdrop of our
 * own, just text laid over art.
 */
.toast {
  display: flex;
  align-items: center;
  min-width: 340px;
  max-width: 440px;
  min-height: 54px;
  padding: 0.35rem 1.9rem 0.45rem 4.6rem;
  background: transparent no-repeat center / 100% 100%;
  border: 0;
  filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.55));
  font-size: 0.85rem;
  line-height: 1.25;
  color: #ead9b5;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  cursor: pointer;

  /* The letter and warning plates are parchment, so they read dark. */
  &--letter,
  &--alert {
    color: #33261a;
    text-shadow: none;
  }
}

@media (max-width: 720px) {
  .topbar { flex-wrap: wrap; gap: 0.5rem; }
  .topbar__nav { order: 3; width: 100%; }
  .villagebar { top: unset; position: static; }
}
</style>
